/**
 * Leads v2 — pure UI helpers shared by the מתעניינים tab, kanban, cards,
 * modals and the home widgets. No Firebase, no Redux.
 */
import { toIsraelDate, formatHebrewDate } from "../../utils/dateUtils";
import {
  normalizeIsraeliPhoneE164,
  formatIsraeliPhoneLocal,
  LEAD_ARCHIVED_STATUSES,
  LEAD_SOURCE_CHANNELS,
  getLeadSourceChannel,
} from "../../utils/leadContract";

export const LEADS_VIEW_STORAGE_KEY = "clinikit_leads_view";

/** Hex → rgba string for status tints (statuses store hex colors). */
export const hexToRgba = (hex, alpha) => {
  if (typeof hex !== "string") return `rgba(107,114,128,${alpha})`;
  const m = hex.replace("#", "");
  const full = m.length === 3 ? m.split("").map((c) => c + c).join("") : m;
  const n = parseInt(full, 16);
  if (Number.isNaN(n) || full.length !== 6) return `rgba(107,114,128,${alpha})`;
  const r = (n >> 16) & 255;
  const g = (n >> 8) & 255;
  const b = n & 255;
  return `rgba(${r},${g},${b},${alpha})`;
};

/** CSS custom properties for one status (text / bg / border) — single source. */
export const statusStyleVars = (status) => ({
  "--ld-status": status?.color || "#6b7280",
  "--ld-status-bg": hexToRgba(status?.color, 0.12),
  "--ld-status-border": hexToRgba(status?.color, 0.32),
});

export const isArchivedStatus = (value) => LEAD_ARCHIVED_STATUSES.includes(value);

export const isOpenLead = (lead) =>
  !!lead && lead.isDeleted !== true && !isArchivedStatus(lead.status) && !lead.convertedToPatientId;

/** Israel "today" bounds as Date instants (start inclusive, end exclusive). */
export const israelDayBounds = (now = new Date()) => {
  const z = toIsraelDate(now);
  const start = new Date(z.getFullYear(), z.getMonth(), z.getDate(), 0, 0, 0, 0);
  const end = new Date(start);
  end.setDate(end.getDate() + 1);
  // z is a zoned "wall clock" Date; compare wall clocks consistently below.
  return { start, end, zonedNow: z };
};

const toZoned = (v) => {
  if (!v) return null;
  const d = toIsraelDate(v);
  return d && !Number.isNaN(d.getTime()) ? d : null;
};

/**
 * Follow-up state for a lead: "overdue" | "today" | "upcoming" | null.
 * Archived / deleted leads never report a follow-up.
 */
export const getFollowUpState = (lead, now = new Date()) => {
  if (!isOpenLead(lead) || !lead.nextFollowUpAt) return null;
  const due = toZoned(lead.nextFollowUpAt);
  if (!due) return null;
  const { start, end } = israelDayBounds(now);
  if (due < start) return "overdue";
  if (due < end) return "today";
  return "upcoming";
};

export const FOLLOW_UP_LABELS = {
  overdue: "באיחור",
  today: "היום",
  upcoming: "מעקב",
};

export const formatFollowUp = (lead) => {
  if (!lead?.nextFollowUpAt) return "";
  const d = toZoned(lead.nextFollowUpAt);
  if (!d) return "";
  const hasTime = d.getHours() !== 0 || d.getMinutes() !== 0;
  return formatHebrewDate(lead.nextFollowUpAt, hasTime ? "dd/MM HH:mm" : "dd/MM/yyyy");
};

export const formatLeadDate = (v, withTime = false) =>
  v ? formatHebrewDate(v, withTime ? "dd/MM/yyyy HH:mm" : "dd/MM/yyyy") : "";

/** Display phone: E.164 → 05X-XXXXXXX, legacy free text as-is. */
export const displayPhone = (phone) => {
  if (!phone) return "";
  return phone.startsWith("+972") ? formatIsraeliPhoneLocal(phone) : phone;
};

/** wa.me link or null when the phone cannot be normalized. */
export const whatsappHref = (phone) => {
  const e164 = normalizeIsraeliPhoneE164(phone);
  return e164 ? `https://wa.me/${e164.replace("+", "")}` : null;
};

export const telHref = (phone) => {
  const e164 = normalizeIsraeliPhoneE164(phone);
  return e164 ? `tel:${e164}` : phone ? `tel:${String(phone).replace(/[^\d+]/g, "")}` : null;
};

/** Phone/email key used to spot a returning lead. */
export const leadIdentityKey = (lead) => {
  if (!lead) return null;
  const e164 = normalizeIsraeliPhoneE164(lead.phone);
  if (e164) return `p:${e164}`;
  if (lead.phone) return `p:${String(lead.phone).replace(/\D/g, "")}`;
  if (lead.email) return `e:${String(lead.email).trim().toLowerCase()}`;
  return null;
};

/** Map identityKey → count across the clinic (deleted excluded). */
export const buildDuplicateMap = (leads) => {
  const map = new Map();
  (leads || []).forEach((l) => {
    if (l.isDeleted === true) return;
    const k = leadIdentityKey(l);
    if (!k) return;
    map.set(k, (map.get(k) || 0) + 1);
  });
  return map;
};

export const sourceChannelLabel = (lead) => {
  const ch = getLeadSourceChannel(lead);
  return LEAD_SOURCE_CHANNELS.find((c) => c.value === ch)?.label || "נוצר ידנית";
};

/** Source filter key: channel for manual/booking/import, key label for webhook. */
export const leadSourceFilterKey = (lead) => {
  const ch = getLeadSourceChannel(lead);
  if (ch === "webhook") return `webhook:${lead?.source?.keyLabel || lead?.source?.keyId || "webhook"}`;
  return ch;
};

export const leadSourceFilterLabel = (key) => {
  if (key.startsWith("webhook:")) return key.slice("webhook:".length) === "webhook" ? "נקלט אוטומטית" : key.slice("webhook:".length);
  return LEAD_SOURCE_CHANNELS.find((c) => c.value === key)?.label || key;
};

/** Normalized search match over name / phone / email / referralSource. */
export const leadMatchesQuery = (lead, q) => {
  if (!q) return true;
  const needle = q.trim().toLowerCase();
  if (!needle) return true;
  const digits = needle.replace(/\D/g, "");
  if (lead.name && lead.name.toLowerCase().includes(needle)) return true;
  if (lead.email && lead.email.toLowerCase().includes(needle)) return true;
  if (lead.referralSource && String(lead.referralSource).toLowerCase().includes(needle)) return true;
  if (digits.length >= 3 && lead.phone) {
    const pd = String(lead.phone).replace(/\D/g, "");
    if (pd.includes(digits) || (digits.startsWith("0") && pd.includes(digits.slice(1)))) return true;
  }
  return false;
};

/** KPI strip numbers (all in memory). */
export const computeLeadKpis = (leads, now = new Date()) => {
  const live = (leads || []).filter((l) => l.isDeleted !== true);
  const nowMs = now.getTime();
  const weekAgo = nowMs - 7 * 86400000;
  const ninetyAgo = nowMs - 90 * 86400000;
  let newThisWeek = 0;
  let dueToday = 0;
  let overdue = 0;
  let inProgress = 0;
  let created90 = 0;
  let converted90 = 0;
  live.forEach((l) => {
    const created = l.createdAt ? new Date(l.createdAt).getTime() : null;
    if (created && created >= weekAgo) newThisWeek += 1;
    if (created && created >= ninetyAgo) {
      created90 += 1;
      if (l.status === "converted" || l.convertedToPatientId) converted90 += 1;
    }
    if (isOpenLead(l)) {
      if (l.status !== "new") inProgress += 1;
      const st = getFollowUpState(l, now);
      if (st === "today") dueToday += 1;
      else if (st === "overdue") overdue += 1;
    }
  });
  const conversionRate = created90 > 0 ? Math.round((converted90 / created90) * 100) : null;
  return { newThisWeek, dueToday, overdue, inProgress, conversionRate, created90, converted90 };
};

/** Sort: follow-up overdue/today first, then status order, then newest. */
export const sortLeads = (leads, statuses, now = new Date()) => {
  const orderOf = (v) => statuses.find((s) => s.value === v)?.order ?? 99;
  const rank = { overdue: 0, today: 1, upcoming: 2 };
  return [...leads].sort((a, b) => {
    const fa = getFollowUpState(a, now);
    const fb = getFollowUpState(b, now);
    const ra = fa ? rank[fa] : 3;
    const rb = fb ? rank[fb] : 3;
    if (ra !== rb) return ra - rb;
    const oa = orderOf(a.status);
    const ob = orderOf(b.status);
    if (oa !== ob) return oa - ob;
    return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
  });
};

/** Combine DatePicker ISO (YYYY-MM-DD) + TimePicker HH:mm into a Date (local). */
export const combineDateTime = (isoDate, hhmm) => {
  if (!isoDate) return null;
  const [y, m, d] = isoDate.split("-").map((x) => parseInt(x, 10));
  if (!y || !m || !d) return null;
  let h = 0;
  let min = 0;
  if (hhmm && /^\d{2}:\d{2}$/.test(hhmm)) {
    [h, min] = hhmm.split(":").map((x) => parseInt(x, 10));
  }
  const date = new Date(y, m - 1, d, h, min, 0, 0);
  return Number.isNaN(date.getTime()) ? null : date;
};

/** Split a stored follow-up (ISO string) into { date: YYYY-MM-DD, time: HH:mm|"" } for the pickers. */
export const splitDateTime = (v) => {
  if (!v) return { date: "", time: "" };
  const d = toZoned(v);
  if (!d) return { date: "", time: "" };
  const pad = (n) => String(n).padStart(2, "0");
  const date = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  const hasTime = d.getHours() !== 0 || d.getMinutes() !== 0;
  return { date, time: hasTime ? `${pad(d.getHours())}:${pad(d.getMinutes())}` : "" };
};

export const therapistDisplayName = (t) =>
  t ? `${t.firstName || ""} ${t.lastName || ""}`.trim() || t.fullName || t.displayName || "" : "";
